import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../components/api';
import { toast } from 'react-toastify';

const useAssiduite = () => {
  const [activeTab, setActiveTab] = useState('etudiants');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [retardsEtudiants, setRetardsEtudiants] = useState([]);
  const [retardsEnseignants, setRetardsEnseignants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Données statiques qui ne changent pas souvent
  const [classes, setClasses] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [modules, setModules] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    classe: '',
    niveau: '',
    filiere: '',
    dateDebut: null,
    dateFin: null
  });

  // État du formulaire
  const [formData, setFormData] = useState({
    etudiant: '',
    enseignant: '',
    matiere: '',
    temps: '',
    motif: ''
  });

  // Refs pour éviter les appels API doubles
  const staticDataLoadedRef = useRef(false);
  const loadingStaticDataRef = useRef(false);

  const resetForm = useCallback(() => {
    setFormData({
      etudiant: '',
      enseignant: '',
      matiere: '',
      temps: '',
      motif: ''
    });
    setSelectedClasse('');
    setSelectedModule('');
    setIsEditing(false);
  }, []);

  // Chargement initial des données statiques (une seule fois avec protection)
  const loadStaticData = useCallback(async () => {
    // Protection contre les appels multiples
    if (staticDataLoadedRef.current || loadingStaticDataRef.current) {
      return;
    }

    loadingStaticDataRef.current = true;
    
    try {
      console.log('🔄 Chargement des données statiques...');
      
      const [classesRes, filieresRes, niveauxRes, enseignantsRes] = await Promise.all([
        api.get('/classe/get-all-classe/'),
        api.get('/filiere/liste-filieres/'),
        api.get('/niveau/liste-niveaux/'),
        api.get('/enseignant/liste-enseignant/')
      ]);

      setClasses(classesRes.data.results || classesRes.data || []);
      setFilieres(filieresRes.data.results || filieresRes.data || []);
      setNiveaux(niveauxRes.data.results || niveauxRes.data || []);
      setEnseignants(enseignantsRes.data.results || enseignantsRes.data || []);
      
      staticDataLoadedRef.current = true;
      setDataLoaded(true);
      
      console.log('✅ Données statiques chargées avec succès');
    } catch (error) {
      // console.error('❌ Erreur lors du chargement des données statiques:');
      toast.error('Erreur lors du chargement des données');
    } finally {
      loadingStaticDataRef.current = false;
    }
  }, []);

  // Chargement des retards (données dynamiques) - optimisé avec dépendances stables
  const loadRetards = useCallback(async () => {
    try {
      console.log('🔄 Chargement des retards...');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        ...(filters.classe && { classe: filters.classe }),
        ...(filters.niveau && { niveau: filters.niveau }),
        ...(filters.filiere && { filiere: filters.filiere }),
        ...(filters.dateDebut && { date_debut: filters.dateDebut.toISOString().split('T')[0] }),
        ...(filters.dateFin && { date_fin: filters.dateFin.toISOString().split('T')[0] })
      });

      const [retardsEtudiantsRes, retardsEnseignantsRes] = await Promise.all([
        api.get(`/assiduite/retard-eleve/?${params}`),
        api.get(`/assiduite/retard-enseignant/?${params}`)
      ]);

      setRetardsEtudiants(retardsEtudiantsRes.data.results || []);
      setTotalPages(Math.ceil((retardsEtudiantsRes.data.count || 0) / 10));
      setRetardsEnseignants(retardsEnseignantsRes.data.results || []);
      
      console.log('✅ Retards chargés avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des retards:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setRetardsEtudiants([]);
        setRetardsEnseignants([]);
        setTotalPages(1);
      } else {
        toast.error('Erreur lors du chargement des retards');
      }
    }
  }, [
    currentPage, 
    filters.classe, 
    filters.niveau, 
    filters.filiere, 
    filters.dateDebut?.toISOString(), 
    filters.dateFin?.toISOString()
  ]);

  // Fonctions pour charger les données spécifiques
  const fetchEtudiantsByClasse = useCallback(async (classeId) => {
    try {
      const response = await api.get(`/assiduite/etudiant-by-class/${classeId}/`);
      setEtudiants(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des étudiants:', error);
      toast.error('Erreur lors du chargement des étudiants');
    }
  }, []);

  const fetchModulesByClasse = useCallback(async (classeId) => {
    try {
      const response = await api.get(`/assiduite/classe/${classeId}/modules/`);
      setModules(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des modules:', error);
      toast.error('Erreur lors du chargement des modules');
    }
  }, []);

  // Actions
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      const endpoint = activeTab === 'etudiants' 
        ? '/assiduite/retard-eleve/'
        : '/assiduite/retard-enseignant/';

      const data = activeTab === 'etudiants' 
        ? {
            etudiant_id: parseInt(formData.etudiant),
            matiere_id: selectedModule ? parseInt(selectedModule) : null,
            temps: parseInt(formData.temps),
            motif: formData.motif || ''
          }
        : {
            enseignant_id: parseInt(formData.enseignant),
            matiere_id: selectedModule ? parseInt(selectedModule) : null,
            temps: parseInt(formData.temps),
            motif: formData.motif || ''
          };

      console.log('🔍 Frontend - Données à envoyer:', { endpoint, data, activeTab });
      console.log('🔍 Frontend - FormData state:', formData);
      console.log('🔍 Frontend - SelectedModule:', selectedModule);

      const response = await api.post(endpoint, data);
      
      console.log('✅ Frontend - Réponse reçue:', response.data);
      
      if (response.data) {
        toast.success('Retard enregistré avec succès');
        setShowModal(false);
        loadRetards();
        resetForm();
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (error) {
      console.error('❌ Frontend - Erreur lors de l\'enregistrement:', error);
      console.error('❌ Frontend - Détails de l\'erreur:', error.response?.data);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement du retard');
    }
  }, [activeTab, formData, selectedModule, loadRetards, resetForm]);

  const handleExport = useCallback(async (format) => {
    try {
      const response = await api.get(`/assiduite/retard-eleve/export/?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `retards_eleves.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  }, []);

  const handleDelete = useCallback((id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      const endpoint = activeTab === 'etudiants' 
        ? `/assiduite/retard-eleve/${itemToDelete}/`
        : `/assiduite/retard-enseignant/${itemToDelete}/`;
      
      console.log('🔍 Suppression - Endpoint:', endpoint, 'ActiveTab:', activeTab);
      
      await api.delete(endpoint);
      toast.success('Retard supprimé avec succès');
      loadRetards();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete, activeTab, loadRetards]);

  const handleEdit = useCallback((retard) => {
    const classeId = classes.find(c => c.libelle === retard.classe_nom)?.id;
    
    if (classeId && activeTab === 'etudiants') {
      setSelectedClasse(classeId.toString());
      fetchEtudiantsByClasse(classeId);
      fetchModulesByClasse(classeId);
    }
    
    setFormData({
      etudiant: activeTab === 'etudiants' ? retard.etudiant.id.toString() : '',
      enseignant: activeTab === 'enseignants' ? retard.enseignant.id.toString() : '',
      matiere: retard.matiere?.id || '',
      temps: retard.temps,
      motif: retard.motif || ''
    });
    
    setSelectedModule(retard.matiere?.id ? retard.matiere.id.toString() : '');
    setIsEditing(true);
    setShowModal(true);
  }, [classes, activeTab, fetchEtudiantsByClasse, fetchModulesByClasse]);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  // Effects optimisés
  useEffect(() => {
    // Chargement des données statiques seulement une fois
    if (!staticDataLoadedRef.current && !loadingStaticDataRef.current) {
      loadStaticData();
    }
  }, []); // Pas de dépendance pour éviter les re-exécutions

  useEffect(() => {
    // Chargement des retards seulement quand les données statiques sont prêtes
    if (dataLoaded) {
      loadRetards().finally(() => setLoading(false));
    }
  }, [dataLoaded, loadRetards]);

  useEffect(() => {
    // Chargement des étudiants et modules par classe
    if (selectedClasse && activeTab === 'etudiants') {
      fetchEtudiantsByClasse(selectedClasse);
      fetchModulesByClasse(selectedClasse);
    } else if (!selectedClasse) {
      setEtudiants([]);
      setModules([]);
    }
  }, [selectedClasse, activeTab, fetchEtudiantsByClasse, fetchModulesByClasse]);

  return {
    // States
    activeTab,
    showModal,
    showDeleteModal,
    itemToDelete,
    isDeleting,
    isEditing,
    retardsEtudiants,
    retardsEnseignants,
    loading,
    classes,
    niveaux,
    filieres,
    modules,
    etudiants,
    enseignants,
    selectedClasse,
    selectedModule,
    currentPage,
    totalPages,
    showFilters,
    filters,
    formData,
    
    // Setters
    setActiveTab,
    setShowModal,
    setShowDeleteModal,
    setSelectedClasse,
    setSelectedModule,
    setCurrentPage,
    setShowFilters,
    setFilters,
    setFormData,
    
    // Actions
    handleSubmit,
    handleExport,
    handleDelete,
    confirmDelete,
    handleEdit,
    formatDate,
    resetForm,
    loadRetards
  };
};

export default useAssiduite; 